import { eventChannel, buffers } from 'redux-saga';
import {
    call,
    put,
    take,
    fork,
    spawn,
    all,
    cancel,
    cancelled,
    takeLatest,
    race,
    select
} from 'redux-saga/effects';

import { startAfter, Timestamp } from 'firebase/firestore';
import { db, fsTools } from 'config/firebase';
import AccessLogService from 'services/access-log.service';

import {
    updateAccessLog,
    appendAccessLog,
    mergeAccessLog,
    accessLogError,
    fetchAccessLogLprDocSuccess,
    fetchAccessLogLprDocError,
    fetchAccessLogDeviceSuccess,
    fetchAccessLogDeviceError,
    fetchAccessLogAccessKeySuccess,
    fetchAccessLogAccessKeyError,
    fetchAccessLogGuardIdentitySuccess,
    fetchAccessLogGuardIdentityError,
    fetchAccessLogApproverInviteSuccess,
    fetchAccessLogApproverInviteError
} from 'store/actions/AccessLog';
import {
    FETCH_MORE_LOGS,
    LOGOUT_USER,
    SET_ACTIVE_USER_ORG,
    FETCH_ACCESS_LOG_LPR_DOC,
    FETCH_ACCESS_LOG_DEVICE,
    FETCH_ACCESS_LOG_ACCESS_KEY,
    FETCH_ACCESS_LOG_GUARD_IDENTITY,
    CLEAR_ACCESS_LOG_GUARD_IDENTITY,
    FETCH_ACCESS_LOG_APPROVER_INVITE,
    CLEAR_ACCESS_LOG_APPROVER_INVITE
} from 'store/actions/types';
import { log } from 'utils/Loggers';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';

const { collection, query, where, orderBy, limit, getDocs, onSnapshot } = fsTools;
const accessLogsRef = collection(db, 'access_logs');
const LIVE_LIMIT = 100;
const ACCESS_LOGS_PAGE_SIZE = 30;
const EXPORT_BATCH_SIZE = 500;

const normalizeLog = doc => {
    const data = doc.data();
    return {
        ...data,
        logItemId: doc.id,
        attempted_at: data.attempted_at
            ? {
                  seconds: data.attempted_at.seconds,
                  nanoseconds: data.attempted_at.nanoseconds
              }
            : null
    };
};

const buildConstraints = (orgId, filters = {}) => {
    const c = [where('org_id', '==', orgId)];
    if (filters.access_type) c.push(where('access_type', '==', filters.access_type));
    if (typeof filters.success === 'boolean')
        c.push(where('success', '==', filters.success));
    if (filters.property_id) c.push(where('property_id', '==', filters.property_id));
    if (filters.consumer_id) c.push(where('consumer_id', '==', filters.consumer_id));
    if (filters.panel)
        c.push(
            filters.panel.type === 'cp'
                ? where('panel_id', '==', filters.panel.id)
                : where('hardware_id', '==', filters.panel.id)
        );
    if (filters.reader) c.push(where('reader_num', '==', filters.reader.number));
    if (filters.plateNumber)
        c.push(where('vehicle_tag', '==', normalizePlate(filters.plateNumber)));
    if (filters.dateFrom)
        c.push(
            where('attempted_at', '>=', Timestamp.fromDate(new Date(filters.dateFrom)))
        );
    if (filters.dateTo)
        c.push(where('attempted_at', '<=', Timestamp.fromDate(new Date(filters.dateTo))));
    c.push(orderBy('attempted_at', 'desc'));
    return c;
};

// Mirrors VehicleHelper.normalizePlate on the backend (sage-nest-api) so a
// search matches however the plate was normalized when the log was written.
const normalizePlate = value => (value ?? '').replace(/\s+/g, '').toUpperCase();

export const hasActiveFilters = (filters = {}) =>
    !!(
        filters.access_type ||
        typeof filters.success === 'boolean' ||
        filters.property_id ||
        filters.consumer_id ||
        filters.dateFrom ||
        filters.dateTo ||
        filters.panel ||
        filters.reader ||
        filters.plateNumber
    );

export function* accessLogsCollectionWatch(orgId, filters = {}) {
    let unsubscribeAccessLogsData;

    // console.log('[AL] accessLogsCollectionWatch START', { orgId, filters });

    const accessLogsCollectionChannel = eventChannel(emit => {
        const accessLogsQuery = query(
            accessLogsRef,
            ...buildConstraints(orgId, filters),
            limit(LIVE_LIMIT)
        );
        // console.log('[AL] onSnapshot attaching', { orgId, filters });
        unsubscribeAccessLogsData = onSnapshot(
            accessLogsQuery,
            snap => {
                // console.log('[AL] onSnapshot fired', { size: snap.size });
                emit({ logs: snap.docs.map(normalizeLog) });
            },
            err => {
                console.log('[AL] onSnapshot ERROR', err?.code, err?.message);
                emit({ error: err });
            }
        );
        return () => {
            console.log('[AL] channel cleanup / unsubscribe', { orgId, filters });
            if (unsubscribeAccessLogsData) {
                unsubscribeAccessLogsData();
            }
        };
    }, buffers.sliding(1));

    let isFirst = true;

    try {
        while (true) {
            const { userSignOut, resettingOrg, accessLogsData } = yield race({
                userSignOut: take(LOGOUT_USER),
                resettingOrg: take(SET_ACTIVE_USER_ORG),
                accessLogsData: take(accessLogsCollectionChannel)
            });

            if (userSignOut) {
                // console.log('[AL] race -> userSignOut, closing');
                yield call([accessLogsCollectionChannel, 'close']);
            } else if (resettingOrg) {
                // console.log('[AL] race -> resettingOrg, closing + return');
                accessLogsCollectionChannel.close();
                return;
            } else if (accessLogsData?.error) {
                // console.log('[AL] race -> error path', accessLogsData.error?.message);
                yield put(accessLogError(accessLogsData.error.message));
                SnackbarNotify.error(
                    'Couldn’t load access logs for this filter. Try a different combination or add a date range.'
                );
            } else {
                const newLogs = accessLogsData.logs;
                // console.log('[AL] race -> data', { isFirst, count: newLogs.length });

                if (isFirst) {
                    isFirst = false;
                    // console.log('[AL] put updateAccessLog (first snapshot replace)');
                    yield put(updateAccessLog(newLogs));
                } else {
                    //
                    // The whole window goes to the reducer, which upserts by id. Filtering
                    // to "newer than the newest we hold" discarded every modification —
                    // a patched log keeps its attempted_at, so it never looked new. The
                    // reducer returns the identical state object when nothing differs, so
                    // an uneventful snapshot still costs no render.
                    //
                    yield put(mergeAccessLog(newLogs));
                }
            }
        }
    } catch (error) {
        log('AccessLog Error: getting live access logs collection data (FS)', {
            error,
            orgId
        });
    } finally {
        console.log('[AL] finally: closing channel', { cancelled: yield cancelled() });
        accessLogsCollectionChannel.close();
    }
}

export function* fetchPaginatedLogsWithFilters({ payload }) {
    const { lastCursor, filters = {}, orgId } = payload;

    // First page (filtered or unfiltered) is delivered by the live listener's first
    // snapshot. Pagination only handles loading older pages via the cursor.
    if (!lastCursor) {
        // console.log(
        //     '[AL] fetchPaginatedLogsWithFilters: no cursor, early return (live owns first page)'
        // );
        return;
    }

    try {
        const cursorTs = new Timestamp(lastCursor.seconds, lastCursor.nanoseconds);
        const snap = yield call(
            getDocs,
            query(
                accessLogsRef,
                ...buildConstraints(orgId, filters),
                startAfter(cursorTs),
                limit(LIVE_LIMIT)
            )
        );
        yield put(appendAccessLog(snap.docs.map(normalizeLog)));
    } catch (err) {
        log('AccessLog Error: paginated query failed (FS)', {
            error: err,
            orgId,
            filters
        });
        yield put(accessLogError(err?.message || 'Failed to load more access logs'));
        SnackbarNotify.error(
            'Couldn’t load more access logs for this filter. Try a different combination or add a date range.'
        );
    }
}

let liveTask = null;

export function* startAccessLogsLive(orgId, filters = {}) {
    if (!orgId) return;
    // console.log('[AL] startAccessLogsLive', { orgId, filters, hadLiveTask: !!liveTask });
    try {
        if (liveTask) {
            yield cancel(liveTask);
            liveTask = null;
        }
        liveTask = yield spawn(accessLogsCollectionWatch, orgId, filters);
    } catch (err) {
        console.log('[AL] startAccessLogsLive ERROR', err?.message);
    }
}

function* handleLiveSession({ payload }) {
    const { lastCursor, filters = {}, orgId } = payload;
    // console.log('[AL] handleLiveSession FETCH_MORE_LOGS', {
    //     lastCursor: !!lastCursor,
    //     filters,
    //     orgId
    // });
    if (!lastCursor) {
        yield call(startAccessLogsLive, orgId, filters);
    }
}

function* watchLiveSession() {
    yield takeLatest(FETCH_MORE_LOGS, handleLiveSession);
}

function* watchLogoutCleanup() {
    while (true) {
        yield take(LOGOUT_USER);
        if (liveTask) {
            yield cancel(liveTask);
            liveTask = null;
        }
    }
}

function* watchOrgSwitchCleanup() {
    while (true) {
        yield take(SET_ACTIVE_USER_ORG);
        // console.log(
        //     '[AL] watchOrgSwitchCleanup -> SET_ACTIVE_USER_ORG, cancelling liveTask',
        //     { hadLiveTask: !!liveTask }
        // );
        if (liveTask) {
            yield cancel(liveTask);
            liveTask = null;
        }
    }
}

export function* watchPaginatedAccessLog() {
    yield takeLatest(FETCH_MORE_LOGS, fetchPaginatedLogsWithFilters);
}

// Fetches EVERY access log matching the given filters for CSV export, by paging
// through the collection in batches with a cursor. Uses the same buildConstraints
// as the live listener / pagination so the export matches the filtered list.
// Intentionally independent of the Redux `accessLog` state, the live listener and
// the on-scroll pagination — it does not touch how the dashboard list loads.
export const fetchAllAccessLogsForExport = async (orgId, filters = {}) => {
    if (!orgId) return [];
    const all = [];
    let cursor = null;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const constraints = [...buildConstraints(orgId, filters)];
        if (cursor) constraints.push(startAfter(cursor));
        constraints.push(limit(EXPORT_BATCH_SIZE));
        const snap = await getDocs(query(accessLogsRef, ...constraints));
        if (snap.empty) break;
        all.push(...snap.docs.map(normalizeLog));
        if (snap.docs.length < EXPORT_BATCH_SIZE) break;
        cursor = snap.docs[snap.docs.length - 1];
    }
    return all;
};

export const getPropertyAccessLogs = async (orgId, propertyId, lastDoc = null) => {
    const constraints = [
        where('org_id', '==', orgId),
        where('property_id', '==', propertyId),
        orderBy('attempted_at', 'desc'),
        limit(ACCESS_LOGS_PAGE_SIZE)
    ];
    if (lastDoc) constraints.push(startAfter(lastDoc));
    const snap = await getDocs(query(accessLogsRef, ...constraints));
    return {
        logs: snap.docs.map(normalizeLog),
        lastDoc: snap.docs[snap.docs.length - 1] ?? null,
        hasMore: snap.docs.length >= ACCESS_LOGS_PAGE_SIZE
    };
};

//
// LPR evidence of an expanded log. Logs written since the switch to `lpr_attempts` carry the
// read as `metadata.lpr_attempt`; only its image is fetched, as a short-lived signed URL (a
// failed lookup just leaves the capture out). Older logs point at their `lpr` document.
//
function* gettingAccessLogLpr({ payload }) {
    const { lprDocId, logId, lprAttempt } = payload;
    try {
        if (lprAttempt) {
            let imageUrl = null;
            try {
                imageUrl = lprAttempt.image_path
                    ? yield call(
                          [AccessLogService, AccessLogService.getLprImageUrl],
                          logId
                      )
                    : null;
            } catch (err) {
                log('AccessLog Error: getting LPR image for Access Log', {
                    error: err,
                    logId
                });
            }
            yield put(
                fetchAccessLogLprDocSuccess({ lprAttempt, imageUrl: imageUrl || null })
            );
            return;
        }

        if (!lprDocId) {
            throw new Error('Missing lprDocId');
        }
        const lprDocRef = fsTools.doc(db, 'lpr', lprDocId);
        const lprDoc = yield call(fsTools.getDoc, lprDocRef);

        if (!lprDoc.exists) {
            yield put(fetchAccessLogLprDocSuccess(null));
            return;
        }

        yield put(
            fetchAccessLogLprDocSuccess({
                ...lprDoc.data()
            })
        );
    } catch (err) {
        log('AccessLog Error: getting LPR doc for Access Log', { error: err, lprDocId });
        fetchAccessLogLprDocError(err.message || 'Failed to get access log LPR doc');
        SnackbarNotify.error(`Couldn't retrieve LPR info. Please try again later.`);
    }
}

function* gettingAccessLogDevice({ payload }) {
    const { deviceNumber, orgId } = payload;
    try {
        if (!deviceNumber || !orgId) {
            throw new Error('Missing required device lookup params');
        }

        const devicesCollectionRef = collection(db, 'devices');

        const deviceQuery = query(
            devicesCollectionRef,
            where('device_number', '==', `${deviceNumber}`.trim()),
            where('org_id', '==', orgId)
        );

        const snapshot = yield call(getDocs, deviceQuery);

        if (snapshot.empty) {
            yield put(fetchAccessLogDeviceSuccess(null));
            return;
        }

        const deviceDoc = snapshot.docs[0];

        yield put(
            fetchAccessLogDeviceSuccess({
                id: deviceDoc.id,
                ...deviceDoc.data()
            })
        );
    } catch (error) {
        yield put(fetchAccessLogDeviceError(error.message || error));
    }
}

//
// Property fallback: some logs (e.g. LPR) arrive without a property_id. We can
// recover it from the access key that granted entry — the one at this org for
// this consumer whose plates array includes the vehicle tag.
//
function* gettingAccessLogAccessKey({ payload }) {
    const { orgId, consumerId, vehicleTag } = payload;
    try {
        if (!orgId || !consumerId || !vehicleTag) {
            throw new Error('Missing required access key lookup params');
        }

        const accessKeysRef = collection(db, 'access_keys');

        const accessKeyQuery = query(
            accessKeysRef,
            where('org_id', '==', orgId),
            where('consumer_id', '==', consumerId),
            where('plates', 'array-contains', vehicleTag)
        );

        const snapshot = yield call(getDocs, accessKeyQuery);

        if (snapshot.empty) {
            yield put(fetchAccessLogAccessKeySuccess(null));
            return;
        }

        const accessKeyDoc = snapshot.docs[0];

        yield put(
            fetchAccessLogAccessKeySuccess({
                id: accessKeyDoc.id,
                ...accessKeyDoc.data()
            })
        );
    } catch (error) {
        yield put(fetchAccessLogAccessKeyError(error.message || error));
    }
}

function* gettingAccessLogGuardIdentity({ payload: uid }) {
    try {
        if (!uid) {
            yield put(fetchAccessLogGuardIdentitySuccess(null));
            return;
        }

        const cachedIdentity = yield select(state => state.access.guardIdentities[uid]);
        if (cachedIdentity) {
            yield put(fetchAccessLogGuardIdentitySuccess(cachedIdentity));
            return;
        }

        const userRef = fsTools.doc(db, 'users', uid);

        const { snapshot } = yield race({
            snapshot: call(fsTools.getDoc, userRef),
            cleared: take(CLEAR_ACCESS_LOG_GUARD_IDENTITY),
            orgChanged: take(SET_ACTIVE_USER_ORG),
            loggedOut: take(LOGOUT_USER)
        });

        // The inspector was closed, the organization changed, or the user logged out.
        if (!snapshot) return;

        if (!snapshot.exists()) {
            yield put(fetchAccessLogGuardIdentitySuccess(null));
            return;
        }

        const user = snapshot.data();

        yield put(
            fetchAccessLogGuardIdentitySuccess({
                uid: snapshot.id,
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email
            })
        );
    } catch (error) {
        log('AccessLog Error: getting guard identity', { error, uid });
        yield put(fetchAccessLogGuardIdentityError(error.message || String(error)));
    }
}

function* gettingAccessLogApproverInvite({ payload: inviteId }) {
    try {
        if (!inviteId) {
            yield put(fetchAccessLogApproverInviteSuccess(null));
            return;
        }

        const cachedInvite = yield select(
            state => state.access.approverInvites[inviteId]
        );
        if (cachedInvite) {
            yield put(fetchAccessLogApproverInviteSuccess(cachedInvite));
            return;
        }

        const inviteRef = fsTools.doc(db, 'invites', inviteId);

        const { snapshot } = yield race({
            snapshot: call(fsTools.getDoc, inviteRef),
            cleared: take(CLEAR_ACCESS_LOG_APPROVER_INVITE),
            orgChanged: take(SET_ACTIVE_USER_ORG),
            loggedOut: take(LOGOUT_USER)
        });

        if (!snapshot) return;

        if (!snapshot.exists()) {
            yield put(fetchAccessLogApproverInviteSuccess(null));
            return;
        }

        const invite = snapshot.data();

        yield put(
            fetchAccessLogApproverInviteSuccess({
                invite_id: snapshot.id,
                first_name: invite.first_name,
                last_name: invite.last_name,
                status: invite.status
            })
        );
    } catch (error) {
        log('AccessLog Error: getting approver invite', { error, inviteId });
        yield put(fetchAccessLogApproverInviteError(error.message || String(error)));
    }
}

export function* getAccessLogLpr() {
    yield takeLatest(FETCH_ACCESS_LOG_LPR_DOC, gettingAccessLogLpr);
}

export function* getAccessLogDevice() {
    yield takeLatest(FETCH_ACCESS_LOG_DEVICE, gettingAccessLogDevice);
}

export function* getAccessLogAccessKey() {
    yield takeLatest(FETCH_ACCESS_LOG_ACCESS_KEY, gettingAccessLogAccessKey);
}

export function* getAccessLogGuardIdentity() {
    yield takeLatest(FETCH_ACCESS_LOG_GUARD_IDENTITY, gettingAccessLogGuardIdentity);
}

export function* getAccessLogApproverInvite() {
    yield takeLatest(FETCH_ACCESS_LOG_APPROVER_INVITE, gettingAccessLogApproverInvite);
}

export default function* rootSaga() {
    yield all([
        fork(watchPaginatedAccessLog),
        fork(watchLiveSession),
        fork(watchOrgSwitchCleanup),
        fork(watchLogoutCleanup),
        fork(getAccessLogLpr),
        fork(getAccessLogDevice),
        fork(getAccessLogAccessKey),
        fork(getAccessLogGuardIdentity),
        fork(getAccessLogApproverInvite)
    ]);
}
